/**
 * Unified RAG: blogs (semantic via embeddings) + books (keyword search).
 * Loads blogEmbeddings.json and bookContent.json from /data/.
 * Combines top 8 results, sends to Gemini.
 * Links to steadfastly.ai for blog posts and book resources.
 */
import { askGemini } from './geminiApi';

const EMBED_MODEL = 'text-embedding-004';
const OUTPUT_DIM = 256;
const EMBEDDINGS_URL = '/data/blogEmbeddings.json';
const BOOK_CONTENT_URL = '/data/bookContent.json';
const SITE_BASE = 'https://steadfastly.ai';

let embeddingsCache = null;
let bookContentCache = null;

async function loadEmbeddings() {
  if (embeddingsCache) return embeddingsCache;
  try {
    const res = await fetch(EMBEDDINGS_URL);
    if (!res.ok) return null;
    const data = await res.json();
    embeddingsCache = Array.isArray(data.embeddings) ? data.embeddings : [];
    return embeddingsCache;
  } catch {
    return null;
  }
}

async function loadBookContent() {
  if (bookContentCache) return bookContentCache;
  try {
    const res = await fetch(BOOK_CONTENT_URL);
    if (!res.ok) return [];
    const data = await res.json();
    bookContentCache = Array.isArray(data.chunks) ? data.chunks : [];
    return bookContentCache;
  } catch {
    return [];
  }
}

async function embedText(apiKey, text, taskType = 'RETRIEVAL_DOCUMENT') {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${EMBED_MODEL}:embedContent?key=${apiKey}`;
  const body = {
    model: `models/${EMBED_MODEL}`,
    content: { parts: [{ text }] },
    taskType,
    outputDimensionality: OUTPUT_DIM,
  };
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Embed API ${res.status}`);
  const data = await res.json();
  return data.embedding?.values ?? [];
}

function cosineSimilarity(a, b) {
  if (!a?.length || !b?.length || a.length !== b.length) return 0;
  let dot = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom ? dot / denom : 0;
}

function keywordScore(query, chunk) {
  const words = query.toLowerCase().split(/\s+/).filter((w) => w.length >= 2);
  const content = ((chunk.content || '') + ' ' + (chunk.keywords || []).join(' ')).toLowerCase();
  let score = 0;
  for (const w of words) {
    const regex = new RegExp(w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const matches = content.match(regex);
    if (matches) score += matches.length;
  }
  return score;
}

async function semanticSearchBlogs(query, topK, apiKey) {
  const embeddings = await loadEmbeddings();
  if (!embeddings?.length || !apiKey) return [];
  const queryEmbedding = await embedText(apiKey, query, 'RETRIEVAL_QUERY');
  if (!queryEmbedding.length) return [];
  const scored = [];
  for (const rec of embeddings) {
    for (const ch of rec.chunks || []) {
      if (!ch.embedding?.length) continue;
      const score = cosineSimilarity(queryEmbedding, ch.embedding);
      scored.push({ text: ch.text, slug: rec.slug, title: rec.title, category: rec.category || 'Blog', date: rec.date || '', score });
    }
  }
  scored.sort((a, b) => b.score - a.score);
  const seen = new Set();
  const out = [];
  for (const s of scored) {
    if (seen.has(s.slug)) continue;
    seen.add(s.slug);
    out.push(s);
    if (out.length >= topK) break;
  }
  return out;
}

async function keywordSearchBooks(query, topK) {
  const chunks = await loadBookContent();
  if (!chunks.length) return [];
  const scored = chunks
    .map((c) => ({ chunk: c, score: keywordScore(query, c) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, topK)
    .map((x) => x.chunk);
  return scored.map((c) => ({
    text: (c.content || '').slice(0, 4000),
    id: c.id,
    book: c.book,
    chapter: c.chapter,
    section: c.section,
    score: keywordScore(query, c),
  }));
}

export function shortBookTitle(full) {
  if (!full) return 'Prove It';
  if (full.includes('Banking Edition')) return 'Prove It: Banking Edition';
  if (full.includes('Global Edition')) return 'Prove It: Global Edition';
  if (full.includes('Reconstruction Mandate')) return 'Prove It: The Reconstruction Mandate';
  return full;
}

export async function queryUnifiedRAG(query, allBlogPosts = []) {
  const trimmed = (query || '').trim();
  if (!trimmed) return { answer: '', sources: [] };

  const apiKey = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GEMINI_API_KEY);
  if (!apiKey) return { answer: 'Ask Steadfastly requires VITE_GEMINI_API_KEY in your .env file.', sources: [] };

  let blogChunks = [];
  try {
    blogChunks = await semanticSearchBlogs(trimmed, 4, apiKey);
  } catch {
    blogChunks = [];
  }

  if (blogChunks.length === 0 && allBlogPosts?.length) {
    const words = trimmed.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
    const scored = allBlogPosts.map((p) => {
      let score = 0;
      const titleLower = (p.title || '').toLowerCase();
      const excerptLower = (p.excerpt || '').toLowerCase();
      const contentSnippet = (p.content || '').slice(0, 800).toLowerCase();
      words.forEach((word) => {
        if (titleLower.includes(word)) score += 4;
        if (excerptLower.includes(word)) score += 3;
        if (contentSnippet.includes(word)) score += 1;
      });
      if (excerptLower.includes(trimmed.toLowerCase()) || titleLower.includes(trimmed.toLowerCase())) score += 6;
      return { post: p, score };
    });
    const topBlogs = scored.filter((x) => x.score > 0).sort((a, b) => b.score - a.score).slice(0, 4).map((x) => x.post);
    blogChunks = topBlogs.map((p) => ({
      text: [p.title, p.excerpt, (p.content || '').slice(0, 4000)].filter(Boolean).join('\n\n'),
      slug: p.slug,
      title: p.title,
      category: p.category || 'Blog',
      date: p.date || '',
      score: 1,
    }));
  }

  let bookChunks = [];
  try {
    bookChunks = await keywordSearchBooks(trimmed, 4);
  } catch {
    bookChunks = [];
  }

  const blogSources = blogChunks.map((c) => ({
    sourceType: 'blog',
    title: c.title,
    slug: c.slug,
    href: `${SITE_BASE}/blog/${c.slug}/`,
    category: c.category || 'Blog',
    date: c.date || '',
    text: c.text,
  }));

  const bookSources = bookChunks.map((c) => ({
    sourceType: 'book',
    title: shortBookTitle(c.book) + (c.chapter ? ` — ${c.chapter}` : ''),
    book: c.book,
    chapter: c.chapter,
    section: c.section,
    id: c.id,
    href: `${SITE_BASE}/resources/book/${c.id ? `#${c.id}` : ''}`,
    text: c.text,
  }));

  const combined = [...blogSources, ...bookSources].slice(0, 8);
  const context = combined
    .map((c) => {
      const header = c.sourceType === 'blog'
        ? `[Blog: ${c.title}] (${c.category}${c.date ? `, ${c.date}` : ''})`
        : `[Book: ${c.title}]`;
      return `${header}\n${c.text}`;
    })
    .join('\n\n---\n\n');

  const prompt = `You are an expert on regulatory compliance and GRC. Answer the user's question using ONLY the provided excerpts from blog posts and Prove It books. Be specific and cite which source your answer comes from (e.g., "According to [Blog: Post Title]..." or "According to Prove It: Banking Edition...").

Excerpts (blogs + books):
${context}

User question: ${trimmed}

Instructions:
- Answer directly and specifically
- Cite the source
- If the excerpts don't contain relevant information, say so
- Keep the answer to 2-4 short paragraphs`;

  try {
    const answer = await askGemini(prompt);
    const sources = combined.slice(0, 6).map((c) => ({
      sourceType: c.sourceType,
      title: c.title,
      slug: c.slug,
      book: c.book,
      chapter: c.chapter,
      section: c.section,
      href: c.href,
      category: c.category,
      date: c.date,
    }));
    return { answer, sources };
  } catch (err) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      console.error('Unified RAG error:', err);
    }
    return {
      answer: 'We found relevant sources but could not generate an answer right now. Try browsing the sources below or refine your question.',
      sources: combined.slice(0, 3).map((c) => ({
        sourceType: c.sourceType,
        title: c.title,
        slug: c.slug,
        book: c.book,
        chapter: c.chapter,
        section: c.section,
        href: c.href,
        category: c.category,
        date: c.date,
      })),
    };
  }
}
